import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { FocusScope } from 'react-aria';
import { useTypedDispatch, useTypedSelector } from '../../redux/hooks';
import {
    selectAccountId,
    selectActivePage,
    selectAppSettingsPin,
    selectProvisioningOpen,
    selectShowFeedback,
    updateActivePage
} from '../../redux/slices';
import {
    ChatErrorBoundary,
    ChatSidebar,
    ErrorBoundary,
    Navbar,
    NotificationCentre,
    TabNotifications
} from '../../components';
import { Phone } from '../phone';
import styles from './Home.module.scss';
import CallFeedbackContainer from '../../components/forms/CallFeedback/CallFeedbackContainer';
import KeypadProvider from '../../context/KeypadContext/context';
import Keypad from '../../components/keypad';
import NoChat from '../auth/NoChat';
import { Chat } from '../chat';
import { Settings } from '../settings';
import VideoSideBar from '../../components/sidebars/Video';
import { ToastCenter } from '../../components/toastCenter';
import LoadingSpinner from '../../components/loading/LoadingSpinner';
import { getFocusableElements, getFocusableInputFields } from '../../helpers';
import SettingsList from '../../components/sidebars/settings/SettingsList';
import { HotKeyNavValue } from '../../context/HotKeysContext/context';
import Aside from '../../components/aside';
import HomeReRenderOffLoad from './children/HomeReRenderOffLoad';
import ContactSidebar from '../../components/sidebars/Contact/ContactSidebar';
import UserStatusController from './children/UserStatusController';
import ContactViewerWrapper from '../../modals/ContactViewer';
import PhonebookContactEditorModal from '../../modals/PhonebookContactEditorModal';
import SmartDaillerErrorModal from '../../modals/SmartDiallerError';
import HomeContexProviders from './children/HomeContexProviders';
import Video from '../video';
import CallMidpoint from '../../components/forms/CallMidpoint';
import FeatureContext from '../../context/FeatureProvider/context';
import StatusSocketController from './children/SocketStatusController/SocketStatusController';
import { SettingsPin } from '../settings-pin/SettingsPin';
import WhatsNewBanner from '../../components/banners/WhatsNewBanner';

const ProvisioningContainer = lazy(
    () => import('../../components/Provisioning/ProvisioningContainer')
);
const Design = lazy(() => import('../Design'));

export const isElectron = navigator.userAgent.toLowerCase().includes(' electron/');

interface HomeProps {
    setIsLoaded: (val: boolean) => void;
}

const Home: React.FC<HomeProps> = ({ setIsLoaded }) => {
    const dispatch = useTypedDispatch();

    const user = useTypedSelector(state => state.user);
    const accountId = useTypedSelector(selectAccountId);
    const showProvisioning = useTypedSelector(selectProvisioningOpen);

    // CHAT Related
    const largeFontMode = useTypedSelector(state => state.user.settings.chat.settings?.isLargeFont);

    // SIP Related
    // const showShortcodePage = useTypedSelector(state => state.sip.showShortcodePage);
    const showFeedbackForm = useTypedSelector(selectShowFeedback);
    const canCollectCallFeedback = useTypedSelector(state => state.sip.callFeedback.enabled);
    const page = useTypedSelector(selectActivePage);

    // const [contactViewer, setContactViewer] = useState<any>();

    const hasAccountSettingsPin = useTypedSelector(selectAppSettingsPin);

    const atKeypadRef = useRef<HTMLDivElement>(null);
    const atNavBarRef = useRef<HTMLElement>(null);
    const atSidebarRef = useRef<HTMLElement>(null);
    const atPageRef = useRef<HTMLElement>(null);

    let activePage: React.ReactElement | null;
    let activeSidebar: React.ReactElement | null;
    let sectionId;
    let fallback: React.ReactElement | null = null;

    const [settingsPinApproved, setSettingsPinApproved] = React.useState(!hasAccountSettingsPin);

    // clear the path state if logged in with azure

    const useFocus = (ref: HTMLElement) => {
        const focusElement =
            getFocusableInputFields({ container: ref })[0] ||
            getFocusableElements({ container: ref })?.[0];
        if (focusElement) {
            (focusElement as HTMLElement).focus();
        }
    };

    const hotKeyNavigate = (focusTo: HotKeyNavValue) => {
        switch (focusTo) {
            case 'nav':
                if (atNavBarRef.current) {
                    const focusElement = getFocusableElements({
                        container: atNavBarRef.current
                    })?.[0];
                    if (focusElement) {
                        (focusElement as HTMLElement).focus();
                    }
                }
                break;
            case 'sidebar':
                if (atSidebarRef.current) {
                    useFocus(atSidebarRef.current);
                }
                break;
            case 'main':
                if (atPageRef.current) {
                    useFocus(atPageRef.current);
                }
                break;
            case 'keypad':
                if (atKeypadRef.current) {
                    const focusElement = getFocusableElements({
                        container: atKeypadRef.current
                    })?.[0];
                    if (focusElement) {
                        (focusElement as HTMLElement).focus();
                    }
                }
        }
    };

    useEffect(() => {
        const hotKey = e => {
            if (e.code === 'Digit1' && e.shiftKey && e.altKey) {
                e.preventDefault();
                hotKeyNavigate('nav');
            }
            if (e.code === 'Digit2' && e.shiftKey && e.altKey) {
                e.preventDefault();
                hotKeyNavigate('sidebar');
            }
            if (e.code === 'Digit3' && e.shiftKey && e.altKey) {
                e.preventDefault();
                hotKeyNavigate('main');
            }
            if (e.code === 'Digit4' && e.shiftKey && e.altKey) {
                e.preventDefault();
                hotKeyNavigate('keypad');
            }
        };

        window.addEventListener('keydown', hotKey);

        return () => {
            window.removeEventListener('keydown', hotKey);
        };
    }, []);

    switch (page) {
        case 'chat':
            sectionId = 'chat';

            if (user.chat_enabled) {
                activeSidebar = <ChatSidebar />;
                activePage = (
                    <ErrorBoundary errorComponent={<ChatErrorBoundary />}>
                        <Chat />
                    </ErrorBoundary>
                );
            } else {
                activeSidebar = null;
                activePage = <NoChat />;
            }
            break;

        case 'video':
            sectionId = 'meeting';
            activePage = <Video />;
            activeSidebar = <VideoSideBar />;
            fallback = <div className='video-page'>:/</div>;
            break;

        case 'settings':
            sectionId = 'settings';
            activeSidebar = <SettingsList />;
            activePage = <Settings />;
            break;

        case 'settings-pin':
            sectionId = 'settings';
            activeSidebar = null;
            activePage = <SettingsPin setApproved={setSettingsPinApproved} />;
            break;

        case 'design':
            sectionId = 'design';
            activeSidebar = null;
            activePage = <Design />;
            break;

        case 'phone':
        default:
            activeSidebar = <ContactSidebar />;
            sectionId = 'phone';
            activePage = <Phone />;
    }

    const shouldRedirectToSettingsPin = (page === 'settings') && !settingsPinApproved;

    React.useEffect(() => {
        if (shouldRedirectToSettingsPin) {
            dispatch(updateActivePage('settings-pin'));
        }
    }, [page]);

    return (
        <HomeContexProviders hotKeyNavigate={hotKeyNavigate}>
            <UserStatusController />
            <StatusSocketController />
            <HomeReRenderOffLoad setIsLoaded={setIsLoaded} />
            {typeof accountId === 'number' ? (
                <div className={styles.container} data-is-browser={!isElectron || null}>
                    <Suspense>{showProvisioning ? <ProvisioningContainer /> : null}</Suspense>
                    <KeypadProvider>
                        <Keypad ref={atKeypadRef} />
                    </KeypadProvider>
                    <FocusScope>
                        <Navbar ref={atNavBarRef} />
                    </FocusScope>
                    <div>
                        {isElectron ? <WhatsNewBanner /> : null}
                        <main
                            id='main_screen'
                            className={`${page === 'video' ? 'hidden-header ' : ''}${!largeFontMode ? 'small-font' : ''
                                }`}
                        >
                            <Suspense fallback={<LoadingSpinner color='mamba' />}>
                                {activeSidebar ? (
                                    <Aside activeSidebar={activeSidebar} ref={atSidebarRef} />
                                ) : null}
                            </Suspense>
                            <Suspense fallback={fallback}>
                                <section id={sectionId} ref={atPageRef}>
                                    {activePage}
                                </section>
                            </Suspense>
                            {canCollectCallFeedback && showFeedbackForm && (
                                <CallFeedbackContainer />
                            )}
                            <SmartDaillerErrorModal />
                            <PhonebookContactEditorModal />
                            <ContactViewerWrapper />
                            <NotificationCentre />
                            <ToastCenter />
                            <TabNotifications />
                            <CallMidpoint />
                        </main>
                    </div>
                </div>
            ) : null}
        </HomeContexProviders>
    );
};

// const HomeWrapper: React.FC<HomeProps> = ({setIsLoaded}) => {

//     // eslint-disable-next-line @typescript-eslint/no-unused-vars
//     const feature_config = {
//         url: 'https://ff.yay.com/api/frontend', // Your front-end API URL or the Unleash proxy's URL (https://<proxy-url>/proxy)
//         clientKey: 'default:development.b670e1ed0b65ed5d197b0301dbc1c07589a50658b76c43b40de3a090', // A client-side API token OR one of your proxy's designated client keys (previously known as proxy secrets)
//         refreshInterval: 15, // How often (in seconds) the client should poll the proxy for updates
//         appName: 'asteroid', // The name of your application. It's only used for identifying your application
//     };

//     return (
//         <FlagProvider config={feature_config}>
//             <Home setIsLoaded={setIsLoaded} />
//         </FlagProvider>
//     )
// }

export default ({ setIsLoaded }: { setIsLoaded: (val: boolean) => void }) => (
    <FeatureContext>
        <Home setIsLoaded={setIsLoaded} />
    </FeatureContext>
);
